import Phaser from 'phaser';
import { createChipSprite, decomposeBet } from './Chip.js';
import {
  type ShimmerClock,
  type PillToken,
  type ShimmerMode,
  INTENSITY_PEAK_ALPHA,
} from './ShimmerClock.js';
import {
  type ShimmerRenderer,
  createShimmerRenderer,
} from './ShimmerRenderer.js';
import { prefersReducedMotion } from '../feel.js';

const BREATH_AMPLITUDE = 0.05;   // ±5% — chips scale between 0.95 and 1.05.
const BREATH_PERIOD_MS = 3500;   // 3.5-second cycle.

export interface ChipStackVisualOptions {
  scene: Phaser.Scene;
  /** Anchor point in scene coords — base of the stack rests here. */
  anchor: { x: number; y: number };
  /** Display radius of each chip sprite in canvas-buffer pixels. */
  chipRadius: number;
  /** Multiplier from intended-CSS-px to canvas-buffer-px (= devicePixelRatio). */
  dpr: number;
  /** Cap on visible chips — above this we still show the total accurately. */
  maxVisible?: number;
  /** Depth for the chip stack. The total badge is drawn at depth + 1. */
  depth?: number;
  /** Render the dollar-amount badge above the stack (default true). Set
   *  false for fly-by visuals where the badge would clip off-screen. */
  showBadge?: boolean;
  /** Optional shimmer clock — when provided, the badge gets a periodic
   *  gold-wash overlay coordinated with every other money pill on this
   *  scene. Pass undefined for transient/fly-by stacks where shimmer would
   *  chase a moving target. */
  shimmerClock?: ShimmerClock | undefined;
}

/**
 * Renders a stack of chips at an anchor with a pill-shaped total badge above.
 * Used by craps' BetZone (per-zone wager) to render the chip-stacking visual.
 *
 * Caller manages lifecycle — call `destroy()` when the host scene shuts down,
 * or rely on the parent container if the visuals were `add`ed to one.
 */
export class ChipStackVisual {
  private stack: Phaser.GameObjects.Container;
  private badge: Phaser.GameObjects.Container;
  private badgeBg: Phaser.GameObjects.Graphics;
  private badgeText: Phaser.GameObjects.Text;
  private opts: Required<ChipStackVisualOptions>;
  private currentCents = 0;
  private badgeOverride: string | null = null;
  private shimmerClock: ShimmerClock | undefined;
  private shimmerToken: PillToken | undefined;
  private shimmerRenderer: ShimmerRenderer | undefined;
  private shimmerRendererMode: Exclude<ShimmerMode, 'off'> | undefined;
  private shimmerBounds: { width: number; height: number; radius: number } | undefined;
  private shimmerSuppressed = false;
  private breathDisabled: boolean;

  constructor(opts: ChipStackVisualOptions) {
    this.opts = {
      maxVisible: 6,
      depth: 0,
      showBadge: true,
      // shimmerClock stays nullable — Required<> would force it to be defined,
      // so we keep the merge but stash the clock on a separate field.
      ...opts,
    } as Required<ChipStackVisualOptions>;
    this.shimmerClock = opts.shimmerClock;
    const { scene, anchor, dpr } = this.opts;

    this.stack = scene.add.container(anchor.x, anchor.y).setDepth(this.opts.depth);

    this.badgeBg = scene.add.graphics();
    this.badgeText = scene.add.text(0, 0, '', {
      fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
      fontSize: `${Math.round(11 * dpr)}px`,
      color: '#f4d35e',
      fontStyle: 'bold',
    })
      .setOrigin(0.5)
      .setShadow(0, 1, 'rgba(0,0,0,0.55)', 2, false, true);
    this.badge = scene.add.container(anchor.x, anchor.y, [this.badgeBg, this.badgeText])
      .setDepth(this.opts.depth + 1)
      .setVisible(false);

    if (this.shimmerClock) {
      this.shimmerToken = this.shimmerClock.register();
      scene.events.on(Phaser.Scenes.Events.UPDATE, this.tickShimmer);
    }

    this.breathDisabled = prefersReducedMotion();
    if (!this.breathDisabled) {
      scene.events.on(Phaser.Scenes.Events.UPDATE, this.tickBreath);
    }
  }

  /** Re-anchor (e.g. after a layout reflow). */
  setAnchor(x: number, y: number) {
    this.opts.anchor = { x, y };
    this.stack.setPosition(x, y);
    // Badge position is recomputed on the next render in setCents().
    if (this.currentCents > 0) this.setCents(this.currentCents);
  }

  /** Current anchor (bottom-chip center) — useful for spawning fly chips at
   *  the left of an existing stack without poking at private state. */
  getAnchor(): { x: number; y: number } {
    return { x: this.opts.anchor.x, y: this.opts.anchor.y };
  }

  /** Chip radius this visual was built with — needed by callers spawning
   *  transient stacks that should match this stack's visual size. */
  getChipRadius(): number {
    return this.opts.chipRadius;
  }

  /** Override the auto-formatted "$N" badge text. Pass null to restore the
   *  default. The override applies on the next render — call setCents (or
   *  rely on setCents being re-invoked elsewhere) to see the change.
   */
  setBadgeOverride(text: string | null) {
    if (this.badgeOverride === text) return;
    this.badgeOverride = text;
    if (this.currentCents > 0) this.setCents(this.currentCents);
  }

  /** Render the stack for the given total in cents. Pass 0 to clear. */
  setCents(cents: number) {
    this.currentCents = cents;
    this.stack.removeAll(true);
    if (cents <= 0) {
      this.badge.setVisible(false);
      this.shimmerRenderer?.clear();
      return;
    }

    const STACK_OFFSET = Math.max(3, Math.round(this.opts.chipRadius * 0.22));
    const flat: number[] = [];
    outer: for (const { denom, count } of decomposeBet(cents)) {
      for (let i = 0; i < count; i++) {
        flat.push(denom);
        if (flat.length >= this.opts.maxVisible) break outer;
      }
    }
    flat.forEach((denom, i) => {
      const sprite = createChipSprite(this.opts.scene, denom, this.opts.chipRadius);
      sprite.setPosition(0, -i * STACK_OFFSET);
      this.stack.add(sprite);
    });

    if (!this.opts.showBadge) {
      this.badge.setVisible(false);
      return;
    }

    const dollars = Math.round(cents / 100);
    const text = this.badgeOverride ?? `$${dollars.toLocaleString()}`;
    this.badgeText.setText(text);
    const padX = Math.round(8 * this.opts.dpr);
    const padY = Math.round(3 * this.opts.dpr);
    const tw = this.badgeText.width;
    const th = this.badgeText.height;
    const bw = tw + padX * 2;
    const bh = th + padY * 2;
    const radius = bh / 2;
    this.shimmerBounds = { width: bw, height: bh, radius };
    // Existing renderer (if any) was sized to the prior bounds — recreate it
    // on the next tick by zeroing the cached mode. setCents is called
    // infrequently enough (mostly on bet change + relayout) that this cost is
    // not a concern.
    if (this.shimmerRenderer) {
      this.shimmerRenderer.destroy();
      this.shimmerRenderer = undefined;
      this.shimmerRendererMode = undefined;
    }
    this.badgeBg.clear();
    this.badgeBg.fillStyle(0x0a0a0a, 0.95);
    this.badgeBg.fillRoundedRect(-bw / 2, -bh / 2, bw, bh, radius);
    this.badgeBg.lineStyle(Math.max(1, 1.5 * this.opts.dpr), 0xd4af37, 0.9);
    this.badgeBg.strokeRoundedRect(-bw / 2, -bh / 2, bw, bh, radius);
    const stackTopY = -(flat.length - 1) * STACK_OFFSET;
    const a = this.opts.anchor;
    this.badge.setPosition(a.x, a.y + stackTopY - this.opts.chipRadius - bh / 2 - 4);
    this.badge.setVisible(true);
  }

  /** Set alpha on both stack and badge — used to dim non-active hands. */
  setAlpha(alpha: number) {
    this.stack.setAlpha(alpha);
    this.badge.setAlpha(alpha);
  }

  /**
   * Pulse the chips + badge red ~3x to signal an insufficient-bankroll
   * action attempt. Visual companion to BankrollPill.flashInsufficientFunds.
   */
  flashInsufficientFunds() {
    const flashColor = 0xff5d5d;
    const scene = this.opts.scene;
    const chips = this.stack.list as Phaser.GameObjects.Sprite[];
    const setRed = (red: boolean) => {
      for (const chip of chips) {
        if (typeof (chip as { setTint?: unknown }).setTint === 'function') {
          if (red) chip.setTint(flashColor);
          else chip.clearTint();
        }
      }
      this.badgeText.setColor(red ? '#ff5d5d' : '#f4d35e');
    };
    this.shimmerSuppressed = true;
    this.shimmerRenderer?.clear();
    setRed(true);
    for (let i = 1; i <= 5; i++) {
      const delay = i * 90;
      const red = i % 2 === 1 ? false : true;
      scene.time.delayedCall(delay, () => setRed(red));
    }
    // Total flash duration: 5 ticks * 90ms = 450ms. Lift suppression slightly
    // after to avoid a visible re-shimmer mid-fade.
    scene.time.delayedCall(5 * 90 + 50, () => {
      this.shimmerSuppressed = false;
    });
  }

  private syncShimmerRenderer(): void {
    if (!this.shimmerClock) return;
    if (!this.shimmerBounds) return;

    const mode = this.shimmerClock.getConfig().mode;
    if (mode === 'off') {
      if (this.shimmerRenderer) {
        this.shimmerRenderer.destroy();
        this.shimmerRenderer = undefined;
        this.shimmerRendererMode = undefined;
      }
      return;
    }

    if (this.shimmerRenderer && this.shimmerRendererMode === mode) return;

    if (this.shimmerRenderer) {
      this.shimmerRenderer.destroy();
      this.shimmerRenderer = undefined;
    }
    this.shimmerRenderer = createShimmerRenderer(mode, {
      scene: this.opts.scene,
      parent: this.badge,
      bounds: this.shimmerBounds,
      // depth=1 inside the badge container — sits above bg (depth 0). Phaser
      // containers don't auto-sort by depth, so the gfx is appended last in
      // child order anyway, but keeping the explicit depth makes the
      // "shimmer above bg" intent visible at the call site.
      depth: 1,
    });
    this.shimmerRendererMode = mode;
  }

  private tickBreath = (): void => {
    if (this.currentCents <= 0) return;
    const t = this.opts.scene.time.now;
    const scale = 1 + BREATH_AMPLITUDE * Math.sin((2 * Math.PI * t) / BREATH_PERIOD_MS);
    this.stack.setScale(scale);
  };

  private tickShimmer = (): void => {
    if (!this.shimmerClock || this.shimmerToken === undefined) return;
    if (!this.shimmerBounds) {
      this.shimmerRenderer?.clear();
      return;
    }
    this.syncShimmerRenderer();
    if (!this.shimmerRenderer) return;

    if (this.shimmerSuppressed || !this.badge.visible) {
      this.shimmerRenderer.clear();
      return;
    }

    const screenW = this.opts.scene.scale.width;
    const phase = this.shimmerClock.localPhase(this.shimmerToken, this.badge.x, screenW);
    if (phase === null) {
      this.shimmerRenderer.clear();
      return;
    }
    const peakAlpha = INTENSITY_PEAK_ALPHA[this.shimmerClock.getConfig().intensity];
    this.shimmerRenderer.render(phase, peakAlpha);
  };

  /** Move the chip stack + badge to a new anchor with a smooth tween. */
  tweenTo(x: number, y: number, durationMs: number): Promise<void> {
    this.opts.anchor = { x, y };
    return new Promise<void>((resolve) => {
      const stackTargets = { x, y };
      this.opts.scene.tweens.add({
        targets: this.stack,
        x: stackTargets.x,
        y: stackTargets.y,
        duration: durationMs,
        ease: 'Cubic.Out',
        onComplete: () => {
          // Re-render badge at new anchor since its position is derived in setCents.
          if (this.currentCents > 0) this.setCents(this.currentCents);
          resolve();
        },
      });
      // Tween the badge in parallel so it moves with the stack.
      this.opts.scene.tweens.add({
        targets: this.badge,
        x,
        duration: durationMs,
        ease: 'Cubic.Out',
      });
    });
  }

  /** Tear down all visuals. Idempotent. */
  destroy() {
    if (!this.breathDisabled) {
      this.opts.scene.events.off(Phaser.Scenes.Events.UPDATE, this.tickBreath);
    }
    if (this.shimmerClock) {
      this.opts.scene.events.off(Phaser.Scenes.Events.UPDATE, this.tickShimmer);
    }
    if (this.shimmerRenderer) {
      this.shimmerRenderer.destroy();
      this.shimmerRenderer = undefined;
    }
    if (this.shimmerClock && this.shimmerToken !== undefined) {
      this.shimmerClock.unregister(this.shimmerToken);
      this.shimmerToken = undefined;
    }
    this.stack.destroy();
    this.badge.destroy();
  }
}
